import {AwAppShell, AwBadge, AwButton, AwCrest, AwErrorState, AwLoadingState, AwMetric, AwPageHeader, AwPanel, AwPriorityPill, AwProgress, AwReadinessRing, AwStatusPill, AwWarTableRoute} from '@ascension-tracker/ascension-ui';
import {useProjectDashboard, useReleaseReadiness, useRunValidation} from '../hooks/useTasks.js';

const readinessLabels: Record<string, string> = {build_verified: 'Build Verified', automated_tests: 'Automated Tests', manual_qa: 'Manual QA', performance: 'Performance', security_scan: 'Security Scan', release_notes: 'Release Notes'};

function formatDate(value: string | null) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
}

function relativeTime(value: string | null) {
  if (!value) return 'No activity yet';
  const diffMs = Date.now() - new Date(value).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function ProjectOverview({projectKey}: {projectKey: string}) {
  const dashboard = useProjectDashboard(projectKey);
  if (dashboard.error) return <AwAppShell projectKey={projectKey}><AwErrorState>{(dashboard.error as Error).message}</AwErrorState></AwAppShell>;
  if (!dashboard.data) return <AwAppShell projectKey={projectKey}><AwLoadingState/></AwAppShell>;
  return <ProjectOverviewBody projectKey={projectKey} data={dashboard.data} onRefresh={() => dashboard.refetch()} refreshing={dashboard.isFetching}/>;
}

function ProjectOverviewBody({projectKey, data, onRefresh, refreshing}: {projectKey: string; data: NonNullable<ReturnType<typeof useProjectDashboard>['data']>; onRefresh: () => void; refreshing: boolean}) {
  const {project, taskCounts, blockers, milestones, stages, readinessPct, activity} = data;
  const readiness = useReleaseReadiness(project.currentReleaseId);
  const runValidation = useRunValidation(project.currentReleaseId ?? '');
  const healthy = taskCounts.overdue === 0 && blockers.length === 0;
  const stageTiles = stages.map(stage => ({environment: stage.environment, state: stage.state as 'pending' | 'active' | 'completed' | 'failed'}));

  return (
    <AwAppShell
      projectKey={projectKey}
      quickActions={<>
        <AwButton type="button" onClick={() => { globalThis.location.assign(`/projects/${projectKey}/releases`); }}>Plan Release</AwButton>
        <AwButton type="button" variant="secondary" disabled={!project.currentReleaseId} onClick={() => runValidation.mutate()}>Run Validation</AwButton>
      </>}
    >
      <AwPageHeader
        eyebrow={`Project: ${project.projectKey}`}
        title="Overview"
        actions={<>
          <span className="overview-updated">Last updated: {relativeTime(activity[0]?.createdAt ?? null)}</span>
          <AwButton type="button" variant="secondary" onClick={onRefresh} disabled={refreshing} aria-label="Refresh">⟳</AwButton>
          <AwButton type="button" onClick={() => { globalThis.location.assign(`/projects/${projectKey}/releases`); }}>Plan Release</AwButton>
        </>}
      >
        <p>High level health, status, and progress for {project.displayName}.</p>
      </AwPageHeader>

      <div className="overview-grid overview-grid--merged">
        <AwPanel className="overview-health-card">
          <AwCrest size="lg" label={project.projectKey}/>
          <h2>Project Health</h2>
          <p className="overview-health">
            <AwStatusPill state={healthy ? 'completed' : 'active'}>{healthy ? 'Good' : 'Needs Attention'}</AwStatusPill>
          </p>
          <div className="overview-facts overview-facts--row">
            <AwMetric value={taskCounts.total} label="Tasks"/>
            <AwMetric value={taskCounts.completed} label="Completed"/>
            <AwMetric value={blockers.length} label="Blockers"/>
            <AwMetric value="—" label="Scope Change"/>
          </div>
        </AwPanel>
        <AwPanel className="overview-readiness">
          <h2>Deployment Readiness</h2>
          {project.currentReleaseId ? (
            <>
              <div className="overview-readiness__body">
                <AwReadinessRing value={readinessPct} label="Ready"/>
                <ul className="overview-readiness__checklist">
                  {(readiness.data ?? []).map(check => (
                    <li key={check.checkKey}>
                      <AwStatusPill state={check.state === 'passed' ? 'completed' : check.state === 'failed' ? 'failed' : 'pending'}>{readinessLabels[check.checkKey] ?? check.checkKey}</AwStatusPill>
                    </li>
                  ))}
                </ul>
              </div>
              <AwButton type="button" variant="secondary" onClick={() => runValidation.mutate()} disabled={runValidation.isPending}>Run Validation</AwButton>
            </>
          ) : <p className="task-detail__muted">No active release to validate yet.</p>}
        </AwPanel>
        <AwPanel className="overview-route-card">
          <h2>War Table Release Route</h2>
          {stageTiles.length === 0 ? <p className="task-detail__muted">No active release — the route will appear once a release candidate exists.</p> : <AwWarTableRoute stages={stageTiles} targetLabel={<a href={`/projects/${projectKey}/releases`}>View Release Command →</a>}/>}
        </AwPanel>
      </div>

      <div className="overview-grid overview-grid--triad">
        <AwPanel>
          <h2>Recent Activity</h2>
          {activity.length === 0 ? <p className="task-detail__muted">No activity yet.</p> : (
            <ul className="overview-list">
              {activity.map((event, index) => (
                <li key={index}>
                  <span>{event.action.replace(/[._]/g, ' ')}</span>
                  <small>{formatDate(event.createdAt)}</small>
                </li>
              ))}
            </ul>
          )}
        </AwPanel>
        <AwPanel>
          <h2>Open Blockers <AwBadge>{blockers.length}</AwBadge></h2>
          {blockers.length === 0 ? <p className="task-detail__muted">No open blockers.</p> : (
            <ul className="overview-list">
              {blockers.map(blocker => (
                <li key={blocker.id}>
                  <span>{blocker.title}</span>
                  <AwPriorityPill priority={blocker.priority}/>
                  <small>{blocker.assigneeName ?? 'Unassigned'}</small>
                </li>
              ))}
            </ul>
          )}
        </AwPanel>
        <AwPanel>
          <h2>Milestone Progress</h2>
          {milestones.length === 0 ? <p className="task-detail__muted">No milestones with tasks yet.</p> : (
            <ul className="overview-list overview-list--milestones">
              {milestones.map(milestone => (
                <li key={milestone.id}>
                  <div>
                    <strong>{milestone.name}</strong>
                    <small>{formatDate(milestone.startDate)} – {formatDate(milestone.targetDate)}</small>
                  </div>
                  <AwProgress value={milestone.total ? Math.round((milestone.completed / milestone.total) * 100) : 0}/>
                </li>
              ))}
            </ul>
          )}
        </AwPanel>
      </div>
    </AwAppShell>
  );
}
